import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/masters/denominations` (v0.5① 日報機能 増分1、D-837)。
 * 金種 (denomination_master) と、そのまとめ単位 (denomination_units、例: 100円棒金=50枚) を管理する。
 * バックエンド: apps/api/src/routes/denomination-masters.ts。設計: docs/design/v05-daily-report-detail.md §3-12。
 *
 * ⚠️ この2マスタはテナント全体で共有される定義 (店舗別ではない) — SkillMastersPage.tsx の
 * business-functions/position-levels 等と同型のため、書込みは daily_report_config:manage の
 * all_stores スコープに限定する (バックエンドの requireTenantWideManage と対応)。
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
// ⚠️ ネイティブスピナーは抑制する (D-211 で指摘済みのパターン、KpiTargetsPage.tsx 等15ファイルの既存慣行に倣う)
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type DenominationUnit = {
  unit_id: string;
  denomination_id: string;
  unit_code: string;
  pieces_per_unit: number;
  effective_from: string;
};
type Denomination = {
  denomination_id: string;
  currency_code: string;
  face_value: number;
  kind: 'coin' | 'bill';
  sort_order: number;
  is_active: boolean;
  effective_from: string;
  units: DenominationUnit[];
};
type DenominationDraft = {
  id: string | null;
  currency_code: string;
  face_value: string;
  kind: 'coin' | 'bill';
  sort_order: string;
  is_active: boolean;
};
type UnitDraft = { id: string | null; denominationId: string; unit_code: string; pieces_per_unit: string };

const CURRENCY_CODE_RE = /^[A-Z]{3}$/;

const emptyDenominationDraft = (sortOrder: number): DenominationDraft => ({
  id: null, currency_code: 'JPY', face_value: '', kind: 'coin', sort_order: String(sortOrder), is_active: true,
});

const kindLabel = (k: 'coin' | 'bill') => (k === 'coin' ? '硬貨' : '紙幣');

/**
 * バックエンド (routes/denomination-masters.ts の DenominationWriteSchema) の
 * `z.number().int().min(0).max(99999)` と揃えたクライアント側の事前バリデーション。
 * サーバー送信前に弾くことで、不正な sort_order が無音で 0 に丸められるのを防ぐ。
 */
export const isValidDenominationDraft = (draft: DenominationDraft): boolean => {
  const faceValue = Number(draft.face_value);
  const sortOrder = Number(draft.sort_order);
  return CURRENCY_CODE_RE.test(draft.currency_code.trim().toUpperCase())
    && Number.isInteger(faceValue) && faceValue > 0
    && Number.isInteger(sortOrder) && sortOrder >= 0 && sortOrder <= 99999;
};

export const isValidUnitDraft = (unitDraft: UnitDraft): boolean => {
  const piecesPerUnit = Number(unitDraft.pieces_per_unit);
  return unitDraft.unit_code.trim() !== '' && unitDraft.unit_code.trim().length <= 50
    && Number.isInteger(piecesPerUnit) && piecesPerUnit > 0;
};

export function DenominationMastersPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // §8-50型の権限昇格を防ぐため、書込みは all_stores スコープを持つユーザーのみ許可する
  // (routes/denomination-masters.ts の requireTenantWideManage と対応)。
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'daily_report_config', 'manage');

  const [draft, setDraft] = useState<DenominationDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Denomination | null>(null);
  const [unitDraft, setUnitDraft] = useState<UnitDraft | null>(null);
  const [deleteUnitTarget, setDeleteUnitTarget] = useState<DenominationUnit | null>(null);

  const { data: denominations = [], isLoading, isError } = useQuery<Denomination[]>({
    queryKey: ['denominations'], queryFn: () => apiFetch('/masters/denominations'),
  });

  const saveMut = useMutation({
    mutationFn: (d: DenominationDraft) => {
      const payload: Record<string, unknown> = {
        currency_code: d.currency_code.trim().toUpperCase(),
        face_value: Number(d.face_value),
        kind: d.kind,
        sort_order: Number(d.sort_order),
        is_active: d.is_active,
      };
      return d.id
        ? apiFetch(`/masters/denominations/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/masters/denominations', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['denominations'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/denominations/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['denominations'] }); setDeleteTarget(null); toast.success('削除しました'); },
    // ⚠️ units が残っている場合はフロント側で事前にconfirmDisabledにしているが、他ユーザーが
    // 同時にunitを追加した場合等の競合で409 (DENOMINATION_UNITS_EXIST) が返りうるため、
    // サーバーメッセージをそのままtoast表示する。
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const unitSaveMut = useMutation({
    mutationFn: (d: UnitDraft) => {
      const payload = { unit_code: d.unit_code.trim(), pieces_per_unit: Number(d.pieces_per_unit) };
      return d.id
        ? apiFetch(`/masters/denomination-units/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch(`/masters/denominations/${d.denominationId}/units`, { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['denominations'] }); setUnitDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const unitDeleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/denomination-units/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['denominations'] }); setDeleteUnitTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteUnitTarget(null); },
  });

  const canSaveDraft = !!draft && isValidDenominationDraft(draft);
  const canSaveUnit = !!unitDraft && isValidUnitDraft(unitDraft);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">金種（現金カウント）</h2>
          <p className="text-sm text-text-sub mt-1">現金カウント・金種明細で使う額面とまとめ単位 (棒金等) を登録します。</p>
          {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (登録・編集には全店舗管理権限が必要です)。</p>}
        </div>
        {canManage && (
          <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyDenominationDraft(denominations.length))}>
            金種を追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">金種の取得に失敗しました。</p>
      ) : denominations.length === 0 ? (
        <EmptyState message="金種がまだありません" description={canManage ? '「金種を追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-3">
          {denominations.map((d) => (
            <li key={d.denomination_id} className="border border-surface-border rounded-lg bg-white p-4">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">
                      {d.face_value.toLocaleString()} {d.currency_code}
                    </span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{kindLabel(d.kind)}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">並び順: {d.sort_order}</span>
                    {!d.is_active && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">無効</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" aria-label="金種を編集" onClick={() => setDraft({
                      id: d.denomination_id, currency_code: d.currency_code, face_value: String(d.face_value),
                      kind: d.kind, sort_order: String(d.sort_order), is_active: d.is_active,
                    })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                    <button type="button" aria-label="金種を削除" onClick={() => setDeleteTarget(d)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>

              <div className="mt-3 border-t border-surface-line pt-3">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <span className="text-xs text-text-sub">まとめ単位</span>
                  {canManage && (
                    <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />}
                      onClick={() => setUnitDraft({ id: null, denominationId: d.denomination_id, unit_code: '', pieces_per_unit: '' })}>
                      まとめ単位を追加
                    </Button>
                  )}
                </div>
                {d.units.length === 0 ? (
                  <p className="text-xs text-text-sub">まとめ単位はまだありません。</p>
                ) : (
                  <ul className="space-y-1.5">
                    {d.units.map((u) => (
                      <li key={u.unit_id} className="flex items-center justify-between gap-2 flex-wrap bg-surface-sunken rounded px-2 py-1.5">
                        <span className="text-xs text-text-body break-words">
                          {u.unit_code}: 1{u.unit_code}={u.pieces_per_unit.toLocaleString()}枚
                        </span>
                        {canManage && (
                          <div className="flex items-center gap-1 shrink-0">
                            {/* ⚠️ CLAUDE.md §9 (design-check.js tapTargetSize): 44px未満は不可。
                                入れ子リストで見た目上は小さく見せたいが、タップ領域自体は縮めない。 */}
                            <button type="button" aria-label="まとめ単位を編集" onClick={() => setUnitDraft({
                              id: u.unit_id, denominationId: d.denomination_id, unit_code: u.unit_code, pieces_per_unit: String(u.pieces_per_unit),
                            })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={12} /></button>
                            <button type="button" aria-label="まとめ単位を削除" onClick={() => setDeleteUnitTarget(u)}
                              className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={12} /></button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.id ? '金種を編集' : '金種を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveDraft || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">通貨コード (ISO 4217、3文字)</label>
              <input value={draft.currency_code} onChange={(e) => setDraft({ ...draft, currency_code: e.target.value.toUpperCase() })}
                maxLength={3} className={inputCls} placeholder="JPY" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">額面</label>
              <input type="number" min={1} step={1} value={draft.face_value} onChange={(e) => setDraft({ ...draft, face_value: e.target.value })} className={numberInputCls} placeholder="例: 100" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">種別</label>
              <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v as 'coin' | 'bill' })}>
                <SelectTrigger aria-label="種別"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coin">硬貨</SelectItem>
                  <SelectItem value="bill">紙幣</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" min={0} max={99999} step={1} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={numberInputCls} />
            </div>
            <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
              <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} className="w-4 h-4" />
              有効
            </label>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="金種を削除"
        message={deleteTarget && deleteTarget.units.length > 0
          ? `「${deleteTarget.face_value.toLocaleString()} ${deleteTarget.currency_code}」には ${deleteTarget.units.length} 件のまとめ単位があります。先にまとめ単位を削除してください。`
          : `「${deleteTarget?.face_value.toLocaleString()} ${deleteTarget?.currency_code}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        confirmDisabled={!!deleteTarget && deleteTarget.units.length > 0}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.denomination_id)} />

      <Modal open={!!unitDraft} title={unitDraft?.id ? 'まとめ単位を編集' : 'まとめ単位を追加'} onClose={() => setUnitDraft(null)} size="sm"
        footer={unitDraft && (
          <>
            <Button variant="secondary" onClick={() => setUnitDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveUnit || unitSaveMut.isPending} loading={unitSaveMut.isPending} onClick={() => unitSaveMut.mutate(unitDraft)}>保存</Button>
          </>
        )}>
        {unitDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">単位名 (例: 棒金)</label>
              <input value={unitDraft.unit_code} onChange={(e) => setUnitDraft({ ...unitDraft, unit_code: e.target.value })} maxLength={50} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">1単位あたりの枚数</label>
              <input type="number" min={1} step={1} value={unitDraft.pieces_per_unit} onChange={(e) => setUnitDraft({ ...unitDraft, pieces_per_unit: e.target.value })} className={numberInputCls} placeholder="例: 50" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteUnitTarget} title="まとめ単位を削除"
        message={`「${deleteUnitTarget?.unit_code}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={unitDeleteMut.isPending}
        onClose={() => setDeleteUnitTarget(null)} onConfirm={() => deleteUnitTarget && unitDeleteMut.mutate(deleteUnitTarget.unit_id)} />
    </div>
  );
}
